import { Component, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import * as moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { CalculationSubject, Dictionary, DictionaryType } from '../../../../../domain/dictionary.model';
import { ValidationError } from '../../../../../domain/error.model';
import { Address } from '../../../../../domain/party.domain';
import {
    InsuredProductMotor,
    MotorConfigurationStep,
    VehicleDefinitionMode
} from '../../../../../domain/product.domain';
import { LsnErrorStateMatcher } from '../../../../shared/middleware/error-state-matcher';
import { EurotaxService } from '../../../service/eurotax.service';
import { UtilsService } from '../../../service/utils.service';
import { AsideAction } from '../../../store/aside.action';
import { DictionaryState } from '../../../store/dictionary.state';
import { ErrorsState } from '../../../store/errors.state';
import { OfferAction } from '../../../store/offer.action';
import { OfferState } from '../../../store/offer.state';

@Component({
    selector: 'app-motor-config-aside',
    templateUrl: './motor-config-aside.component.html',
    styleUrls: ['./motor-config-aside.component.scss']
})
export class MotorConfigAsideComponent implements OnInit, OnDestroy {

    subscriptions: Subscription[] = [];
    @Select(DictionaryState.getDictionaries) dictionaries$: Observable<{ [key: string]: Dictionary }>;
    dictionaryTypes = DictionaryType;
    calculationSubject = CalculationSubject;
    errors: ValidationError[] = [];

    product: InsuredProductMotor;
    calculated: InsuredProductMotor;

    get productCalculated() {
        const json1 = UtilsService.stringify(this.product);
        const json2 = UtilsService.stringify(this.calculated);
        return json1 === json2;
    }

    motorConfigurationStep = MotorConfigurationStep;
    vehicleDefinitionMode = VehicleDefinitionMode;

    matchers = {
        continuation: new LsnErrorStateMatcher(this, 'continuation.selected'),
        previousPolicyNumber: new LsnErrorStateMatcher(this, 'continuation.policyNumber'),
        insuranceStartDate: new LsnErrorStateMatcher(this, 'insuranceStartDate'),
        insuranceEndDate: new LsnErrorStateMatcher(this, 'insuranceEndDate'),
        driverListGlobal: new LsnErrorStateMatcher(this, 'driverList'),
        driverList: [],
        calculation: new LsnErrorStateMatcher(this, 'calculation'),

        leasing: {
            name: new LsnErrorStateMatcher(this, 'leasing.name'),
        },
        vehicleDefinition: {
            licensePlate: new LsnErrorStateMatcher(this, 'vehicleDefinition.licensePlate'),
            vin: new LsnErrorStateMatcher(this, 'vehicleDefinition.vin'),
            vehicleValue: new LsnErrorStateMatcher(this, 'vehicleDefinition.vehicleValue'),
            firstRegistrationDate: new LsnErrorStateMatcher(this, 'vehicleDefinition.firstRegistrationDate'),
            mileage: new LsnErrorStateMatcher(this, 'vehicleDefinition.mileage'),
            vehicleUnusualUsage: new LsnErrorStateMatcher(this, 'vehicleDefinition.vehicleUnusualUsage'),

        },
        autocasco: {
            sumOfInsurance: new LsnErrorStateMatcher(this, 'autocasco.sumOfInsurance'),
            claimSettlementMode: new LsnErrorStateMatcher(this, 'autocasco.claimSettlementMode'),
        },

        paymentDefinition: {
            installments: new LsnErrorStateMatcher(this, 'paymentDefinition.installments'),
            deferredPayment: new LsnErrorStateMatcher(this, 'paymentDefinition.deferredPayment'),
        }
    }

    get firstRegistrationDate() {
        if (this.product.configuration.vehicleDefinition.productionYear) {
            return new Date(this.product.configuration.vehicleDefinition.productionYear, 0, 1);
        } else {
            return null;
        }
    }

    get eurotaxValueDisabled() {
        return !this.product.configuration.vehicleDefinition.eurotax.typeId
            || !this.product.configuration.vehicleDefinition.firstRegistrationDate
            || !this.product.configuration.vehicleDefinition.mileage;
    }

    set leasing(value) {
        if (value === true && this.product.configuration.leasing.address === null) {
            this.product.configuration.leasing.address = new Address();
        }
        this.product.configuration.leasing.selected = value;
    }

    get continuation() {
        return this.product.configuration.continuation.selected
            ? CalculationSubject.CONTINUATION
            : CalculationSubject.NEW_POLICY;
    }

    set continuation($event: any) {
        this.product.configuration.continuation.selected = $event === CalculationSubject.CONTINUATION;
    }

    set insuranceStartDate(value) {
        this.product.configuration.insuranceStartDate = value;
        this.product.configuration.insuranceEndDate = value !== null
            ? moment(value).add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD')
            : null;
    };

    get hasNextButton() {
        return ([
            MotorConfigurationStep.BASIC,
            MotorConfigurationStep.CLAIMS,
        ] as MotorConfigurationStep[]).indexOf(this.product.configuration.configurationStep) !== -1;
    }

    get hasBackButton() {
        return ([
            MotorConfigurationStep.CLAIMS,
            MotorConfigurationStep.DETAILS,
        ] as MotorConfigurationStep[]).indexOf(this.product.configuration.configurationStep) !== -1;
    }

    constructor(private store: Store,
                private eurotaxService: EurotaxService,
    ) {
    }


    ngOnInit(): void {
        this.subscriptions.push(
            this.store.select(OfferState.getMotorProduct).subscribe(product => this.setProduct(product)),
            this.store.select(OfferState.getMotorCalculated).subscribe(calculated => this.calculated = cloneDeep(calculated)),
            this.store.select(ErrorsState.getAsideErrors).subscribe(errors => this.errors = errors)
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private setProduct(product: InsuredProductMotor) {
        while (product.configuration.driverList.length > this.matchers.driverList.length) {
            const length = this.matchers.driverList.length;
            this.matchers.driverList.push({
                licenseDate: new LsnErrorStateMatcher(this, `driverList[${length}].licenseDate`),
                noLicense: new LsnErrorStateMatcher(this, `driverList[${length}].noLicense`),
                insuranceClaimHistory: {
                    thirdPartyLiability: {
                        continuityYears: new LsnErrorStateMatcher(this, `driverList[${length}].insuranceClaimHistory.thirdPartyLiability.continuityYears`),
                        claims2y: new LsnErrorStateMatcher(this, `driverList[${length}].insuranceClaimHistory.thirdPartyLiability.claims2y`),
                        claims5y: new LsnErrorStateMatcher(this, `driverList[${length}].insuranceClaimHistory.thirdPartyLiability.claims5y`),
                    },
                    autocasco: {
                        continuityYears: new LsnErrorStateMatcher(this, `driverList[${length}].insuranceClaimHistory.autocasco.continuityYears`),
                        claims2y: new LsnErrorStateMatcher(this, `driverList[${length}].insuranceClaimHistory.autocasco.claims2y`),
                        claims5y: new LsnErrorStateMatcher(this, `driverList[${length}].insuranceClaimHistory.autocasco.claims5y`),
                    }
                }
            })
        }
        this.product = cloneDeep(product);
    }

    setNoLicense(index: number, $event: any) {
        this.product.configuration.driverList[index].noLicense = $event;
        if ($event === true) {
            this.product.configuration.driverList[index].licenseDate = null;
        }
    }

    close() {
        this.store.dispatch(AsideAction.CloseRight());
    }

    next() {
        const offer = this.store.selectSnapshot(OfferState.getOffer);
        switch (this.product.configuration.configurationStep) {
            case MotorConfigurationStep.BASIC:
                this.store.dispatch(new OfferAction.ValidationMotorBasic(offer.id, this.product));
                break;
            case MotorConfigurationStep.CLAIMS:
                this.store.dispatch(new OfferAction.ValidationMotorClaims(offer.id, this.product));
                break;
        }
    }

    back() {
        switch (this.product.configuration.configurationStep) {
            case MotorConfigurationStep.DETAILS:
                if (this.product.configuration.showUfgConfigurationScreen) {
                    this.product.configuration.configurationStep = MotorConfigurationStep.CLAIMS;
                } else {
                    this.product.configuration.configurationStep = MotorConfigurationStep.BASIC;
                }

                break;
            case MotorConfigurationStep.CLAIMS:
                this.product.configuration.configurationStep = MotorConfigurationStep.BASIC;
                break;
        }
    }

    save() {
        const offer = this.store.selectSnapshot(OfferState.getOffer);
        this.store.dispatch(new OfferAction.SaveMotor(offer.id, this.product));
    }


    calculate() {
        const offer = this.store.selectSnapshot(OfferState.getOffer);
        this.store.dispatch(new OfferAction.CalculateMotor(offer.id, this.product));
    }

    calculateEurotaxValue() {
        const definition = this.product.configuration.vehicleDefinition;
        this.eurotaxService.getValue(definition.eurotax.typeId, definition.firstRegistrationDate, definition.mileage,)
            .subscribe(value => this.product.configuration.vehicleDefinition.vehicleValue = value.valuation.baseSaleValue);
    }
}
